// =============================================================================
// Optim TURPE V2 — ce que courtage#253 et courtage#254 servent sur la structure
// proposée (revue des études du 25/09/2026, lot Front-E).
//
// Ce que le client doit vivre :
// - DEV-2183 : sous « structure déjà optimale », rien n'est « recommandé ». Une
//   autre structure qui ferait gagner un peu est une OPTION chiffrée sous le
//   seuil, avec le libellé servi, et la réserve A18 quand elle coûte plus cher ;
// - DEV-2184 : la structure proposée devant les appels mesurés, classe par
//   classe, avec la CMDPS attendue ; ce que coûterait de couvrir le pic ; un
//   raccordement écarté dit sur combien de pas, son levier, et ce que coûterait
//   de le respecter ; la jambe « avec » qui a retenu la structure « sans » le dit ;
// - DEV-2185 : le plancher affiché est celui qui a été APPLIQUÉ ; les pointes
//   mesurées se lisent sous leur clé neutre, avec leur unité.
//
// Règle de tout ce fichier : un champ servi d'abord, un repli explicite sur une
// étude antérieure ensuite, et une absence qui reste une absence (jamais un 0).
// =============================================================================

import {
    lireChiffre,
    lireStructureChoisie,
} from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    classesAvecReserveDansLaPreuve,
    RESERVE_CLASSE_SUR_L_ANNEE,
} from "@modules/courtage/utils/optim-turpe-v2.qualite"
import {
    estC5,
    formatNombreFr,
    getReservesHorsEntree,
    getUnitePuissance,
    libelleRegimeFiscal,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { libelleFormuleTarifaire } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    formatMontantSansSigne,
    formatMontantSigne,
} from "@modules/courtage/utils/optim-turpe-v2.montant"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageKey,
    OptimTurpeV2Reserve,
} from "@modules/courtage/types/optim-turpe-v2"

/** Codes des réserves que la page rend à côté de leur valeur, hors de la liste générale. */
export const RESERVE_A18 = "structure-recommandee-plus-chere"
export const RESERVE_DENIVELE_RECOMMANDE = "denivele-recommande-sous-la-pointe"
export const RESERVE_RACCORDEMENT_ECARTE = "raccordement-ecarte"
export const RESERVE_SANS_DEPASSEMENTS = "sans-depassements-moins-cher"

/**
 * Le code partagé (moteur, PDF, écran) de la réserve C5 « pic mesuré
 * au-dessus de la puissance souscrite » (Q22 ; courtage
 * `modules/optimturpe/pic_c5.py`, `CODE_PIC_AU_DESSUS_DE_LA_SOUSCRITE`,
 * servie depuis courtage v183 ; nature `constat`, portée `recommandation`,
 * valeurs `pic_kva`, `souscrite_kva`, `puissance_adaptee_kva`,
 * `surcout_annuel_eur`). Il doit rester égal à celui du moteur.
 */
export const CODE_RESERVE_C5_PIC = "c5-pic-au-dessus-de-la-souscrite"

/**
 * Les codes de réserve C5 dont le libellé servi remplace le titre « Rien à
 * changer sur l'acheminement ». Liste fermée : un code absent d'ici ne
 * remplace jamais un titre.
 */
export const CODES_RESERVE_TITRE_C5: readonly string[] = [CODE_RESERVE_C5_PIC]

/**
 * Le libellé de la réserve C5 de Q22 : le `label` SERVI par le moteur, et lui
 * seul (décision Q22 : la réserve est servie par le moteur). L'écran ne
 * compose aucune phrase à partir des `valeurs` : ce texte ne serait pas un
 * libellé validé (revue du lot 3 bis). `null` hors C5, sans réserve, ou sans
 * libellé servi.
 */
export function reserveTitreC5(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    if (!etude || !estC5(etude)) return null
    const reserve = (etude.reserves ?? []).find((r) =>
        CODES_RESERVE_TITRE_C5.includes(r.code)
    )
    return reserve?.label?.trim() || null
}

/** Le montant du C5 sous le pic, tel que le moteur l'a chiffré (DEV-2422). */
export type MontantC5 = {
    /** La puissance qui lève le risque (`puissance_adaptee_kva`). */
    puissanceKva: number
    /**
     * Le surcoût annuel de CETTE puissance à formule actuelle
     * (`surcout_annuel_eur`), ou `null` : sans chiffrage dédié, aucun
     * montant, jamais 0.
     */
    surcoutEur: number | null
    /** « environ » : la période étudiée n'est pas d'un an (Q15, même règle que le moteur). */
    environ: boolean
    /**
     * La phrase SERVIE de la formule recalculée (« Avec la formule courte
     * utilisation à quatre postes, passer à 11 kVA coûterait 43 € HT de plus
     * par an sur l'acheminement. »), lue dans le libellé de la réserve, ou
     * `null` ; jamais composée ici.
     */
    phraseFormuleRecalculee: string | null
}

/** Le début de la phrase servie de la formule recalculée (courtage `pic_c5.py`). */
const DEBUT_PHRASE_FORMULE_RECALCULEE = "Avec la formule "

/**
 * Le montant du C5 sous le pic (contrat v1.3, règle du montant C5, décision
 * du 19/10 n° 1) : lu sur la réserve servie et sur le chiffrage dédié de la
 * puissance adaptée à formule actuelle (courtage v190, DEV-2422), jamais sur
 * l'économie de la combinaison retenue par l'optimisation. Une `cause` servie
 * sur `chiffrage_puissance_adaptee` fait foi : aucun montant. `null` hors C5,
 * sans réserve ou sans puissance adaptée servie.
 */
export function montantC5(
    etude: OptimTurpeV2Etude | null | undefined
): MontantC5 | null {
    if (!etude || !estC5(etude)) return null
    const reserve = (etude.reserves ?? []).find(
        (r) => r?.code === CODE_RESERVE_C5_PIC
    )
    const valeurs = (reserve?.valeurs ?? {}) as Record<string, unknown>
    const adaptee = nombreFini(valeurs.puissance_adaptee_kva)
    if (adaptee === null) return null
    const cause = etude.chiffrage_puissance_adaptee?.cause
    const sansChiffrage = typeof cause === "string" && cause.trim() !== ""
    const servi = nombreFini(valeurs.surcout_annuel_eur)
    const recalculee = valeurs.formule_recalculee as
        | { surcout_annuel_eur?: unknown }
        | null
        | undefined
    const montantRecalcule = nombreFini(recalculee?.surcout_annuel_eur)
    const label = reserve?.label?.trim() ?? ""
    const debut = label.indexOf(DEBUT_PHRASE_FORMULE_RECALCULEE)
    return {
        puissanceKva: adaptee,
        surcoutEur:
            !sansChiffrage && servi !== null && servi > 0 ? servi : null,
        environ: etude.periode?.est_annuelle !== true,
        phraseFormuleRecalculee:
            montantRecalcule !== null && montantRecalcule > 0 && debut >= 0
                ? label.slice(debut).trim()
                : null,
    }
}

function nombreFini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * Les motifs de surcoût écrits pour le C4 et le C2 : couvrir ou assumer des
 * dépassements, une marge qui « couvre » la puissance maximale atteinte. Sur
 * un C5, aucun ne se dit (D18, R-T9 : plus de vocabulaire des dépassements ;
 * DEV-2423 : l'entête C4 fuyait sur le C5 en production le 18/10).
 */
export const MOTIFS_SURCOUT_HORS_C5: readonly string[] = [
    "couverture_du_pic",
    "arbitrage_des_depassements",
    "marge_de_securite",
]

/**
 * Le motif de surcoût servi que la page peut écrire, ou `null` : sur un C5,
 * jamais un motif de `MOTIFS_SURCOUT_HORS_C5`.
 */
export function motifDeSurcoutAffichable(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    if (!etude) return null
    const motif = lireChiffre(etude).etatMotif
    if (!motif) return null
    if (estC5(etude) && MOTIFS_SURCOUT_HORS_C5.includes(motif)) return null
    return motif
}

/**
 * Le libellé de la réserve C5 quand elle TIENT LIEU DE TITRE à la carte de
 * tête, ou `null` : la carte la met en titre à la place de « Rien à changer
 * sur l'acheminement » (déjà optimale, option sous le seuil, surcoût), jamais
 * sur une économie, une économie non chiffrée ou une étude saisie (revue de
 * nuit du 15/10/2026, E4 et S9 : la conclusion ne la redit pas, l'historique
 * la nomme « Pic au-dessus de la souscrite »).
 */
export function reserveC5EnTitre(
    etude: OptimTurpeV2Etude | null | undefined
): string | null {
    const libelle = reserveTitreC5(etude)
    if (!etude || !libelle) return null
    const chiffre = lireChiffre(etude)
    if (chiffre.estScenarioLibre) return null
    if (chiffre.montantServi === false || chiffre.etat === "montant_non_servi")
        return null
    if (
        chiffre.etat === "economie" ||
        chiffre.etat === "gain_par_depassements_evites"
    )
        return null
    return libelle
}

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * « 1 358 € » : valeur absolue, le sens est porté par les mots de la phrase
 * (« de plus », « de moins », « ferait gagner ») : convention de la page,
 * revue du 08/10, R1.
 */
function euros(valeur: number): string {
    return formatMontantSansSigne(valeur) ?? ""
}

function reserve(
    etude: OptimTurpeV2Etude,
    code: string
): OptimTurpeV2Reserve | null {
    return (etude.reserves ?? []).find((r) => r.code === code) ?? null
}

// -----------------------------------------------------------------------------
// DEV-2183 : l'option sous le seuil
// -----------------------------------------------------------------------------

export type OptionSousLeSeuil = {
    /** La phrase de l'option, servie par le moteur ou composée en repli. */
    libelle: string
    /** Vrai quand la phrase vient de `restitution.recommandation.libelle`. */
    servie: boolean
    /** La réserve A18, sans le mot « recommandée », quand l'option coûte plus cher. */
    reserve: string | null
}

/** La structure proposée diffère-t-elle de l'actuelle ? (repli d'une étude antérieure) */
function structureChangee(etude: OptimTurpeV2Etude): {
    change: boolean
    ftaProposee: string | null
    ftaChange: boolean
} {
    const a = etude.actuelle?.structure_tarifaire
    const m = etude.meilleure?.structure_tarifaire
    const ftaA = a?.formule_tarifaire_acheminement?.fta ?? null
    const ftaM = m?.formule_tarifaire_acheminement?.fta ?? null
    const pA = a?.puissance_souscrite_soutirage
    const pM = m?.puissance_souscrite_soutirage
    const change =
        ftaM !== null &&
        (ftaA !== ftaM ||
            (pA?.puissance ?? null) !== (pM?.puissance ?? null) ||
            JSON.stringify(pA?.denivele ?? null) !==
                JSON.stringify(pM?.denivele ?? null))
    return { change, ftaProposee: ftaM, ftaChange: ftaA !== ftaM }
}

/**
 * La réserve A18 sous « déjà optimale ». Le moteur l'écrit « La structure
 * recommandée coûte N € de plus » : sous cet état, la structure n'est pas
 * recommandée (DEV-2183), la phrase est donc composée depuis ses valeurs.
 */
function reserveA18SansRecommandee(etude: OptimTurpeV2Etude): string | null {
    // Vague wording du 14/10/2026 (Q5) : une saisie se dit « Vos puissances »,
    // une combinaison non recommandée « l'option chiffrée ».
    const choisie = lireStructureChoisie(etude)
    const sujet = choisie ? "Vos puissances" : "L'option chiffrée"
    const a18 = reserve(etude, RESERVE_A18)
    if (!a18) return null
    // Revue du lot 3 : la phrase se compose à partir du code et des valeurs
    // servis, jamais en réécrivant le texte du moteur. Le moteur sert A18 quand
    // la nette est entre −50 € et 0 (décision A18 du 21/09) : sans nette
    // négative servie, la phrase ne porte pas de montant. Q14 (revue de nuit
    // du 15/10/2026, E12) : le montant s'écrit signé et HT, comme sur la
    // carte de tête (« Option chiffrée : −12 € HT … »).
    const nette = fini(a18.valeurs?.nette_eur)
    const montant =
        nette !== null && nette < 0 ? formatMontantSigne(nette, "HT") : null
    const ecart = montant
        ? `${sujet} : ${montant} face au contrat actuel sur la période analysée.`
        : `${sujet} ${choisie ? "coûteraient" : "coûterait"} plus cher que le contrat actuel sur la période analysée.`
    return `${ecart} Garder le contrat actuel reste le meilleur coût.`
}

/**
 * L'option sous le seuil, ou `null` quand l'état n'est pas « déjà optimale »,
 * quand la structure actuelle est conservée, ou quand rien n'est chiffré.
 *
 * Le libellé servi (`recommandation.libelle`) fait foi. Sur une étude
 * antérieure à courtage#254, qui ne sert pas le bloc, la phrase est composée
 * avec les mêmes mots depuis la nette et le seuil servis.
 */
export function lireOptionSousLeSeuil(
    etude: OptimTurpeV2Etude
): OptionSousLeSeuil | null {
    const chiffre = lireChiffre(etude)
    if (chiffre.etat !== "deja_optimale") return null
    const servie = etude.recommandation ?? null
    const reserveA18 = reserveA18SansRecommandee(etude)
    if (servie && servie.statut) {
        if (servie.statut !== "option_sous_le_seuil") return null
        const libelle = servie.libelle?.trim()
        if (!libelle) return null
        return { libelle, servie: true, reserve: reserveA18 }
    }
    // Repli : une étude qui ne sert pas encore le statut.
    const { change, ftaProposee, ftaChange } = structureChangee(etude)
    const nette = chiffre.netteEur
    if (!change || nette === null) return null
    // Sous « déjà optimale », aucune démarche n'est conseillée : la page ne dit
    // pas « comme proposé » (DEV-2311, contrôle du 05/10).
    // 05/10/2026 : la formule par son libellé, son code entre parenthèses.
    const geste = ftaChange
        ? `Passer en ${ftaProposee ? libelleFormuleTarifaire(ftaProposee) : ftaProposee}`
        : "Modifier les puissances souscrites"
    let libelle: string
    if (nette > 0) {
        libelle = `${geste} ferait gagner ${euros(nette)} sur la période, sous le seuil de ${euros(chiffre.seuilEur ?? 0)}.`
    } else if (nette < 0) {
        libelle = `${geste} coûterait ${euros(nette)} de plus sur la période ; garder le contrat actuel reste le meilleur coût.`
    } else {
        libelle = `${geste} ne changerait pas le coût sur la période.`
    }
    return { libelle, servie: false, reserve: reserveA18 }
}

/** Ce que la page peut dire de la structure proposée. */
export type StatutStructure =
    | "recommandee"
    | "option_sous_le_seuil"
    | "structure_actuelle"
    | "surcout"
    | null

const STATUTS_SERVIS: ReadonlyArray<Exclude<StatutStructure, null>> = [
    "recommandee",
    "option_sous_le_seuil",
    "structure_actuelle",
    "surcout",
]

/**
 * Le statut de la structure proposée : `restitution.recommandation.statut`
 * servi par le moteur (courtage#254) d'abord. Sur une étude antérieure, il se
 * déduit de l'état LU (servi, ou déduit par la règle publiée et dit comme tel) :
 * un état de gain recommande, un surcoût non, et « déjà optimale » n'offre
 * qu'une option quand la structure change. Un changement de FTA seul ne vaut
 * jamais recommandation (DEV-2183).
 */
export function lireStatutStructure(etude: OptimTurpeV2Etude): StatutStructure {
    const servi = etude.recommandation?.statut
    if (servi && (STATUTS_SERVIS as readonly string[]).includes(servi)) {
        return servi as StatutStructure
    }
    const chiffre = lireChiffre(etude)
    if (chiffre.estGain) return "recommandee"
    if (chiffre.etat === "surcout") return "surcout"
    if (chiffre.etat === "deja_optimale") {
        return structureChangee(etude).change
            ? "option_sous_le_seuil"
            : "structure_actuelle"
    }
    return null
}

/**
 * Le seuil qui a décidé l'état, écrit pour le client (DEV-2183, recette du
 * 28/09). Le moteur sert la comparaison sur la valeur NON arrondie
 * (`etat_nette_comparee_eur`) et le seuil (`etat_seuil_eur`) ; l'inégalité
 * technique (`etat_inegalite`, « 296,27 > 50 ») n'est plus imprimée telle
 * quelle. `null` quand l'étude ne sert pas les deux montants ou pas l'état :
 * la page ne déduit rien.
 */
export function phraseSeuilDEtat(etude: OptimTurpeV2Etude): string | null {
    const nette = fini(etude.etat_nette_comparee_eur)
    const seuil = fini(etude.etat_seuil_eur)
    if (nette === null || seuil === null) return null
    const regime = libelleRegimeFiscal(lireChiffre(etude).regimeFiscal)
    const montant = (v: number) =>
        `${formatNombreFr(Math.abs(v), 2)}\u00a0${regime}`
    switch (etude.etat) {
        case "economie":
        case "gain_par_depassements_evites":
            return `Économie retenue : ${montant(nette)} avant arrondi, au-dessus du seuil de ${montant(seuil)} en deçà duquel aucune démarche n'est recommandée.`
        case "deja_optimale":
            return `L'écart de ${montant(nette)} reste sous le seuil de ${montant(seuil)} : rien à changer sur l'acheminement.`
        case "surcout":
            return `Surcoût retenu : ${montant(nette)} avant arrondi, au-delà du seuil de ${montant(seuil)} : la combinaison chiffrée coûterait plus cher que le contrat actuel.`
        default:
            return null
    }
}

// -----------------------------------------------------------------------------
// DEV-2184 : la structure proposée devant les appels mesurés
// -----------------------------------------------------------------------------

export type LigneDeniveleRecommande = {
    classe: string
    souscrite: number | null
    pointeMesuree: number | null
    ecart: number | null
    ecartPct: number | null
    verdict: "couvre_la_pointe" | "sous_la_pointe" | null
    cmdpsEur: number | null
    unite: string
}

/** `controles.denivele_recommande[]`, ou `null` quand le moteur ne le sert pas. */
export function lireDeniveleRecommande(
    etude: OptimTurpeV2Etude
): LigneDeniveleRecommande[] | null {
    const lignes = etude.controles?.denivele_recommande
    if (!Array.isArray(lignes) || lignes.length === 0) return null
    const uniteDefaut = getUnitePuissance(etude)
    const lues = lignes
        .filter((l) => typeof l?.classe === "string")
        .map((l) => ({
            classe: String(l.classe),
            souscrite: fini(l.souscrite),
            pointeMesuree: fini(l.pointe_mesuree),
            ecart: fini(l.ecart),
            ecartPct: fini(l.ecart_pct),
            verdict:
                l.verdict === "couvre_la_pointe" ||
                l.verdict === "sous_la_pointe"
                    ? l.verdict
                    : null,
            cmdpsEur: fini(l.cmdps_eur),
            unite: l.unite ?? uniteDefaut,
        }))
    return lues.length > 0 ? lues : null
}

/** La réserve « structure proposée sous la pointe », servie telle quelle. */
export function lireReserveDeniveleRecommande(
    etude: OptimTurpeV2Etude
): string | null {
    return reserve(etude, RESERVE_DENIVELE_RECOMMANDE)?.label?.trim() || null
}

/**
 * Ce que coûterait de couvrir le pic, dans la jambe « avec » (M7).
 * `null` quand le moteur ne sert pas `couverture_du_pic`.
 */
export function lireCouvertureDuPic(etude: OptimTurpeV2Etude): string | null {
    const couverture =
        etude.economie?.assurance_couverture_du_pic?.couverture_du_pic
    if (!couverture || typeof couverture !== "object") return null
    const surcout = fini(couverture.surcout_eur)
    if (surcout === null) {
        return couverture.cause
            ? "Le coût de la couverture des dépassements n'est pas chiffré par cette étude."
            : null
    }
    if (surcout > 0) {
        return `Tout couvrir coûterait ${euros(surcout)} de plus sur la période que de payer les dépassements.`
    }
    if (surcout < 0) {
        return `Tout couvrir coûterait ${euros(surcout)} de moins sur la période que de payer les dépassements.`
    }
    return "Tout couvrir coûterait autant que de payer les dépassements."
}

/**
 * La jambe « avec dépassements » a-t-elle retenu la structure du scénario sans
 * dépassements (courtage#253) ? Rend la phrase à écrire, ou `null`.
 */
export function lireSolutionRetenue(etude: OptimTurpeV2Etude): string | null {
    // Revue témoin du 15/10/2026 (D15) : sous « Économie non chiffrée », la
    // page n'imprime ni écart en euros ni scénario ; la réserve servie revient
    // alors parmi les points à vérifier (`reservesDeLaConclusion`).
    const chiffre = lireChiffre(etude)
    if (chiffre.montantServi === false || chiffre.etat === "montant_non_servi")
        return null
    const solution = etude.conduite?.solution_retenue
    if (solution?.cause !== RESERVE_SANS_DEPASSEMENTS) return null
    const pourquoi =
        reserve(etude, RESERVE_SANS_DEPASSEMENTS)?.label?.trim() ||
        "Sur la période analysée, assumer les dépassements ne fait rien gagner : les puissances qui couvrent la puissance maximale atteinte coûtent moins cher."
    const ecart = fini(solution.ecart_eur)
    const ecartTexte =
        ecart !== null && Math.round(ecart) !== 0
            ? ` Écart sur la période : ${euros(Math.round(ecart))}.`
            : ""
    return `Dans ce scénario, les puissances retenues sont celles du scénario « Couvrir les dépassements ». ${pourquoi}${ecartTexte}`
}

export type EcartementRaccordement = {
    /** « Les appels la dépassent sur 1 pas de dix minutes (0,17 h), un dépassement ponctuel. » */
    pas: string | null
    /** Le levier servi par la réserve. */
    levier: string | null
    /** « Bornée à 500 kW, la structure coûterait 237 € de moins sur la période, dépassements compris. » */
    variante: string | null
}

/**
 * Ce qui fonde l'écartement d'un raccordement pour appels supérieurs (M10), ou
 * `null` quand le moteur ne sert pas ces champs (étude antérieure).
 */
export function lireEcartementRaccordement(
    etude: OptimTurpeV2Etude
): EcartementRaccordement | null {
    const borne = etude.bornes?.raccordement
    if (!borne || borne.statut !== "ecartee") return null
    const r = reserve(etude, RESERVE_RACCORDEMENT_ECARTE)
    const pas = fini(borne.pas_au_dessus)
    const duree = fini(borne.duree_au_dessus_h)
    const variante = borne.variante_bornee ?? null
    const ecartVariante = fini(variante?.ecart_vs_recommandation_eur)
    if (pas === null && !r?.levier && ecartVariante === null) return null

    let textePas: string | null = null
    if (pas !== null) {
        const dureeTexte =
            duree !== null ? ` (${formatNombreFr(duree, 2)}\u00a0h)` : ""
        const persistance =
            borne.ecartement_persistant === true
                ? ", un dépassement persistant : la valeur connue est probablement à corriger"
                : borne.ecartement_persistant === false
                  ? ", un dépassement ponctuel : la valeur connue est probablement juste"
                  : ""
        textePas = `La puissance atteinte dépasse cette valeur sur ${formatNombreFr(pas, 0)} relevé${pas >= 2 ? "s" : ""} de dix minutes${dureeTexte}${persistance}.`
    }

    let texteVariante: string | null = null
    const valeur = fini(borne.valeur)
    if (ecartVariante !== null && valeur !== null) {
        const borneTexte = `${formatNombreFr(valeur, 2)}\u00a0${borne.unite ?? getUnitePuissance(etude)}`
        texteVariante =
            ecartVariante === 0
                ? `Limitée à ${borneTexte}, la recommandation coûterait autant sur la période, dépassements compris.`
                : `Limitée à ${borneTexte}, la recommandation coûterait ${euros(ecartVariante)} ${ecartVariante > 0 ? "de plus" : "de moins"} sur la période, dépassements compris.`
    }

    return {
        pas: textePas,
        levier: r?.levier?.libelle?.trim() || null,
        variante: texteVariante,
    }
}

// -----------------------------------------------------------------------------
// DEV-2185 : le plancher appliqué, les pointes sous leur clé neutre
// -----------------------------------------------------------------------------

export type PlancherLu = {
    valeur: number
    provenance: "segment" | "saisie" | null
}

/**
 * Le plancher de modulation APPLIQUÉ, ou `null` quand aucun ne s'applique.
 *
 * Depuis courtage#254, `retenu` est le plancher appliqué et `retenu_provenance`
 * le dit : 37 en C4 (plancher du segment), `null` en C2 sans saisie, rien à
 * afficher. Sur une étude antérieure, `retenu` portait la plus haute puissance
 * recommandée : il ne se lit pas, et seul le défaut du segment sert de repli.
 */
export function lirePlancherRetenu(
    etude: OptimTurpeV2Etude
): PlancherLu | null {
    const bloc = etude.plancher_modulation
    if (!bloc) return null
    if ("retenu_provenance" in bloc) {
        const valeur = fini(bloc.retenu)
        if (valeur === null) return null
        const provenance =
            bloc.retenu_provenance === "segment" ||
            bloc.retenu_provenance === "saisie"
                ? bloc.retenu_provenance
                : null
        return { valeur, provenance }
    }
    const defaut = fini(bloc.segment_defaut)
    return defaut === null ? null : { valeur: defaut, provenance: "segment" }
}

/** « 37 kVA, plancher du segment » : le plancher et sa provenance. */
export function libellePlancherRetenu(
    plancher: PlancherLu,
    unite: string
): string {
    const valeur = `${formatNombreFr(plancher.valeur, 2)}\u00a0${unite}`
    if (plancher.provenance === "segment")
        return `${valeur}, plancher du segment`
    if (plancher.provenance === "saisie") return `${valeur}, valeur saisie`
    return valeur
}

/**
 * Les pointes mesurées par classe : la clé neutre servie par courtage#254
 * d'abord, l'ancienne clé `pointes_mesurees_kw` en repli.
 */
export function lirePointesMesurees(
    etude: OptimTurpeV2Etude
): Partial<Record<OptimTurpeV2PlageKey, number>> | null {
    return etude.pointes_mesurees ?? etude.pointes_mesurees_kw ?? null
}

// -----------------------------------------------------------------------------
// Les réserves de la conclusion
// -----------------------------------------------------------------------------

/**
 * Les réserves de la conclusion, sans celles que la page écrit à côté de leur
 * valeur : la réserve A18 dans l'option sous le seuil, la structure proposée
 * sous la pointe dans le contrôle du dénivelé proposé, le raccordement écarté
 * sur la ligne du raccordement, la structure « sans » retenue dans la jambe
 * « avec » à côté du scénario. Une réserve dont le bloc n'est pas rendu reste
 * dans la liste : aucune n'est perdue.
 */
export function reservesDeLaConclusion(
    etude: OptimTurpeV2Etude,
    {
        preuveSousLesYeux = true,
    }: {
        /**
         * Parcours v0.4 (DEV-2284, lot A) : la preuve est sous « Pourquoi ces
         * puissances », replié. Tant qu'elle n'est pas sous les yeux, la
         * réserve de classe qu'elle porte revient ici (fait 5 : replier ne
         * cache jamais une réserve).
         */
        preuveSousLesYeux?: boolean
    } = {}
): OptimTurpeV2Reserve[] {
    const ailleurs = new Set<string>()
    if (lireOptionSousLeSeuil(etude)?.reserve) ailleurs.add(RESERVE_A18)
    if (lireDeniveleRecommande(etude)) ailleurs.add(RESERVE_DENIVELE_RECOMMANDE)
    if (lireEcartementRaccordement(etude))
        ailleurs.add(RESERVE_RACCORDEMENT_ECARTE)
    if (lireSolutionRetenue(etude)) ailleurs.add(RESERVE_SANS_DEPASSEMENTS)
    // Revue de nuit du 15/10/2026 (E4) : la réserve C5 portée en titre de la
    // carte de tête ne se redit pas sous « Autres remarques ».
    if (reserveC5EnTitre(etude)) ailleurs.add(CODE_RESERVE_C5_PIC)
    // DEV-2181 (M3) : une classe sous-observée sur l'année se lit sur sa ligne
    // de la preuve ; elle ne reste ici que si la preuve ne la montre pas.
    const classesDansLaPreuve = preuveSousLesYeux
        ? classesAvecReserveDansLaPreuve(etude)
        : new Set<string>()
    return getReservesHorsEntree(etude).filter(
        (r) =>
            !ailleurs.has(r.code) &&
            !(
                r.code === RESERVE_CLASSE_SUR_L_ANNEE &&
                classesDansLaPreuve.has(String(r.valeurs?.classe ?? ""))
            )
    )
}
